<script setup lang="ts">
import { ref, nextTick, onBeforeUnmount, onMounted, watch } from "vue";
import { useI18n } from "vue-i18n";

import Cropper from "cropperjs";
import "cropperjs/dist/cropper.css";

const { t } = useI18n();

const emit = defineEmits(["change", "cancel"]);

const props = withDefaults(
  defineProps<{
    aspectRatio?: 1 | 1.333 | 1.777;
    viewMode?: 0 | 1 | 2 | 3;
  }>(),
  {
    viewMode: 1,
  }
);

const fileInput = ref();

const cropper = ref<Cropper | null>(null);
const imageUrl = ref<string | null>(null);
const imageElement = ref<HTMLImageElement | null>(null);

function fileSelect(event: any) {
  const file = (event.target as HTMLInputElement).files?.[0];
  if (file) {
    const reader = new FileReader();
    reader.onload = (e) => {
      imageUrl.value = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  }
}

function cropImage() {
  if (cropper.value) {
    cropper.value.getCroppedCanvas().toBlob((blob) => {
      if (blob) {
        const reader = new FileReader();
        reader.onloadend = () => {
          emit("change", reader.result);
          imageUrl.value = null;
          fileInput.value.value = "";
        };
        reader.readAsDataURL(blob);
      }
    });
  }
}

function cropImageCancel() {
  imageUrl.value = null;
  fileInput.value.value = "";
  emit("cancel");
}

watch(
  () => imageUrl.value,
  async () => {
    await nextTick(async () => {
      if (imageUrl.value) {
        if (cropper.value) cropper.value.destroy();

        await nextTick(() => {
          if (imageElement.value) {
            cropper.value = new Cropper(imageElement.value, {
              aspectRatio: props.aspectRatio ?? NaN,
              viewMode: props.viewMode,
            });
          }
        });
      }
    });
  }
);

onBeforeUnmount(() => {
  if (cropper.value) {
    cropper.value.destroy();
  }
});

onMounted(async () => {
  await nextTick(async () => {
    if (fileInput.value) {
      fileInput.value.click();
    }
  });
});
</script>

<template>
  <section class="crop-component">
    <div class="crop-image">
      <div class="content">
        <div class="crop">
          <template v-if="imageUrl">
            <img ref="imageElement" :src="imageUrl" alt="Source Image" />
          </template>
        </div>
        <div class="actions">
          <button
            type="button"
            class="btn btn-sm btn-default"
            @click="cropImage"
          >
            {{ t("common.labels.save") }}
          </button>
          <button
            type="button"
            class="btn btn-sm btn-default"
            @click="cropImageCancel"
          >
            {{ t("common.labels.cancel") }}
          </button>
        </div>
      </div>
    </div>

    <input ref="fileInput" type="file" @change="fileSelect" accept="image/*" />
  </section>
</template>

<style scoped lang="scss">
.crop-component {
  display: flex;
  width: fit-content;
  flex-direction: row;
  align-items: center;
  gap: 1.5rem;

  .crop-image {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 99999;
    display: flex;
    width: 100vw;
    height: 100vh;
    background: #00000083;

    .content {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      width: calc(70% - (0.75rem * 2));
      height: 30rem;
      margin-block: auto;
      margin-inline: auto;
      background: #fff;
      border-radius: 0.5rem;
      padding: 0.75rem;

      .crop {
        width: 100%;
        height: 28rem;
        overflow: hidden;
        background: #f1f1f1;
      }

      .actions {
        display: flex;
        flex-direction: row;
        gap: 1rem;
      }
    }
  }

  input {
    display: none;
  }

  img {
    width: 100%;
  }
}
</style>
